import { Link } from "react-router-dom";
import { Zap, Shield, Clock, Sparkles, Eye, Heart } from "lucide-react";
import { SEOHead, organizationJsonLd } from "@/components/seo/SEOHead.tsx";

const T = {
  ink: "#0a0a0a", inkSoft: "#6b6b6b", rule: "#e9e6df",
  paper: "#faf8f3", paper2: "#f3efe5", accent: "#ff5b1f",
};

const VALUES = [
  {
    icon: Eye,
    title: "Creator control",
    description: "AI is the engine; you're the driver. Every script word, every storyboard frame, every publish — you approve it. Nothing ships without your OK.",
  },
  {
    icon: Clock,
    title: "Speed without shortcuts",
    description: "From topic to video in a few steps. But we never skip quality gates. Two mandatory review stages ensure nothing reaches your audience that you haven't seen.",
  },
  {
    icon: Shield,
    title: "Honest automation",
    description: "AI disclosure labels on every YouTube upload. We believe AI-generated content should be transparent. It's better for creators and better for viewers.",
  },
  {
    icon: Sparkles,
    title: "Quality at every tier",
    description: "Free users get the same workflow as Agency users — just with different capacity and model tiers. We don't cripple the experience to force upgrades.",
  },
  {
    icon: Heart,
    title: "Built for solo creators",
    description: "TubeForge was designed for one-person operations running faceless channels. No agency bloat, no enterprise meetings. Just you and your content.",
  },
];

const STATS = [
  { value: "2", label: "Free videos to start" },
  { value: "A few steps", label: "Topic to video" },
  { value: "2", label: "Review gates" },
  { value: "15", label: "Video statuses tracked" },
];

export function AboutPage() {
  return (
    <>
      <SEOHead
        title="About — AI Faceless Video Platform"
        description="TubeForge is an AI-powered platform for creating faceless YouTube videos. Learn about our mission, values, and the technology behind the product."
        path="/about"
        jsonLd={organizationJsonLd()}
      />

      {/* Hero */}
      <section className="py-16 md:py-24" style={{ backgroundColor: T.paper }}>
        <div className="mx-auto max-w-4xl px-5 md:px-8 text-center">
          <p className="text-[13px] font-semibold uppercase tracking-widest mb-4" style={{ color: T.accent }}>
            About TubeForge
          </p>
          <h1
            className="text-3xl md:text-5xl font-bold leading-tight mb-6"
            style={{ fontFamily: '"Bricolage Grotesque", "Inter", system-ui', letterSpacing: "-0.03em" }}
          >
            Making faceless video creation <span style={{ color: T.accent }}>fast, honest, and fun</span>
          </h1>
          <p className="text-[16px] md:text-[18px] leading-relaxed max-w-2xl mx-auto" style={{ color: T.inkSoft }}>
            Thousands of creators run successful YouTube channels without ever showing their face.
            The bottleneck is production — scripting, imagery, voiceover, editing. TubeForge removes
            that bottleneck with AI, while keeping you in full creative control.
          </p>
        </div>
      </section>

      {/* Stats strip */}
      <section style={{ backgroundColor: T.paper2, borderTop: `1px solid ${T.rule}`, borderBottom: `1px solid ${T.rule}` }}>
        <div className="mx-auto max-w-5xl px-5 md:px-8 py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {STATS.map((s) => (
              <div key={s.label}>
                <div className="text-3xl md:text-4xl font-bold mb-1" style={{ color: T.accent, letterSpacing: "-0.02em" }}>
                  {s.value}
                </div>
                <div className="text-[13px]" style={{ color: T.inkSoft }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 md:py-20" style={{ backgroundColor: T.paper }}>
        <div className="mx-auto max-w-3xl px-5 md:px-8">
          <h2
            className="text-2xl md:text-3xl font-bold mb-6"
            style={{ fontFamily: '"Bricolage Grotesque", "Inter", system-ui', letterSpacing: "-0.02em" }}
          >
            The problem we saw
          </h2>
          <div className="space-y-5 text-[15px] leading-relaxed" style={{ color: T.inkSoft }}>
            <p>
              Faceless YouTube channels — narrated videos with stock images, AI imagery, or screen recordings — are one of
              the fastest-growing content categories. Creators in niches like history, science, finance, and motivation
              are building audiences of millions without a camera or studio.
            </p>
            <p>
              But the production pipeline is tedious. Writing a script takes hours. Finding or generating matching imagery
              takes more. Recording voiceover, syncing captions, adding music, and editing everything together can easily
              eat an entire day per video. For solo creators trying to publish consistently, it's unsustainable.
            </p>
            <p>
              TubeForge compresses that entire pipeline into a single tool. AI writes the script, generates scene-matched
              images, records voiceover, aligns captions, and renders the final video. The creator's job becomes editorial:
              review the script, approve the storyboard, click publish.
            </p>
            <p>
              We built a two-gate approval system on purpose. AI should accelerate creation, not replace judgment.
              Every video passes through a script review gate and a storyboard review gate before anything is rendered.
              You see exactly what your audience will see — and you change whatever doesn't feel right.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-20" style={{ backgroundColor: T.paper2 }}>
        <div className="mx-auto max-w-5xl px-5 md:px-8">
          <h2
            className="text-2xl md:text-3xl font-bold mb-10 text-center"
            style={{ fontFamily: '"Bricolage Grotesque", "Inter", system-ui', letterSpacing: "-0.02em" }}
          >
            What we believe
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {VALUES.map((v) => {
              const Icon = v.icon;
              return (
                <div
                  key={v.title}
                  className="rounded-2xl p-6"
                  style={{ backgroundColor: "#fff", border: `1px solid ${T.rule}` }}
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center mb-4"
                    style={{ backgroundColor: "rgba(255,91,31,0.08)" }}
                  >
                    <Icon className="w-5 h-5" style={{ color: T.accent }} />
                  </div>
                  <h3 className="text-[15px] font-bold mb-2">{v.title}</h3>
                  <p className="text-[13px] leading-relaxed" style={{ color: T.inkSoft }}>
                    {v.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 text-center" style={{ backgroundColor: T.paper }}>
        <div className="mx-auto max-w-2xl px-5">
          <h2
            className="text-2xl md:text-3xl font-bold mb-4"
            style={{ fontFamily: '"Bricolage Grotesque", "Inter", system-ui', letterSpacing: "-0.02em" }}
          >
            Try it yourself
          </h2>
          <p className="text-[15px] mb-8" style={{ color: T.inkSoft }}>
            2 free videos. No credit card. See the full pipeline in a few steps.
          </p>
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 text-[15px] font-semibold px-7 py-3.5 rounded-xl transition-opacity hover:opacity-90"
            style={{ backgroundColor: T.accent, color: "#fff" }}
          >
            <Zap className="w-4 h-4" /> Start free trial
          </Link>
        </div>
      </section>
    </>
  );
}
